// components/NotificationsDropdown.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, X } from 'lucide-react';
import {
  markNotificationRead,
  markAllNotificationsRead,
  fetchUnreadCount,
  clearNotifications,
} from '../features/notifications/notificationSlice';
import useNotifications from '../hooks/useNotifications';
import axiosInstance from '../api/axiosInstance';

const NotificationsDropdown = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // ✅ Use separate local state for dropdown notifications
  const [dropdownNotifications, setDropdownNotifications] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [dropdownHasMore, setDropdownHasMore] = useState(true);
  
  // Only use unreadCount from Redux (it's global)
  const { unreadCount } = useSelector(
    (state) => state.notifications
  );
  
  const { markAsRead, markAllAsRead } = useNotifications();

  // Fetch unread count periodically (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchUnreadCount());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // ✅ Fetch ALL notifications when dropdown opens (independent from page filter)
  useEffect(() => {
    if (isOpen && dropdownNotifications.length === 0) {
      fetchDropdownNotifications(0);
    }
  }, [isOpen]);

  // ✅ Separate fetch function for dropdown (always fetches ALL notifications)
  const fetchDropdownNotifications = async (offset = 0) => {
    try {
      setDropdownLoading(true);
      
      // ✅ Always fetch ALL notifications for dropdown (no is_read filter)
      const response = await axiosInstance.get('/api/notifications/', {
        params: {
          limit: 20,
          offset: offset,
          // ✅ NO is_read parameter = fetch all
        }
      });

      const newNotifications = response.data.results || response.data;
      
      if (offset === 0) {
        // Replace notifications
        setDropdownNotifications(newNotifications);
      } else {
        // Append for pagination
        setDropdownNotifications(prev => [...prev, ...newNotifications]);
      }
      
      setDropdownHasMore(response.data.next !== null);
      setDropdownLoading(false);
    } catch (error) {
      console.error('Failed to fetch dropdown notifications:', error);
      setDropdownLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      // Mark as read via WebSocket
      markAsRead(notification.id);
      
      // Also mark via REST API as backup
      dispatch(markNotificationRead(notification.id));
      
      // ✅ Update local dropdown state
      setDropdownNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      );
    }
    
    // Navigate based on notification type
    handleNotificationNavigation(notification);
    
    // Close dropdown after navigation
    setIsOpen(false);
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
    dispatch(markAllNotificationsRead());
    
    // ✅ Update local dropdown state
    setDropdownNotifications(prev => 
      prev.map(n => ({ ...n, is_read: true }))
    );
  };

  const handleNotificationNavigation = (notification) => {
    // Navigate to relevant page based on notification type
    switch (notification.notification_type) {
      case 'REPORT_UPDATE':
      case 'REPORT_APPROVED':
      case 'COMMENT_MENTION':
        if (notification.report) {
          navigate(`/reports/${notification.report}/edit`);
        }
        else if (notification.extra_data?.transaction_id) {
          navigate(`/transaction/${notification.extra_data.transaction_id}`);
        }
        break;
      
      case 'CLIENT_QUERY_CREATED':
      case 'CLIENT_QUERY_UPDATE':
        if (notification.query) {
          navigate(`/client-queries/${notification.query}/edit`);
        } else {
          navigate('/clientqueriestable');
        }
        break;
      
      case 'STORE_UPDATE':
        if (notification.extra_data?.store_id) {
          navigate(`/stores/${notification.extra_data.store_id}`);
        } else {
          navigate('/stores');
        }
        break;
      
      case 'EMPLOYEE_UPDATE':
        if (notification.extra_data?.employee_id) {
          navigate(`/employees/${notification.extra_data.employee_id}`);
        } else {
          navigate('/employeecards');
        }
        break;
      
      case 'CASHIER_ASSISTANCE':
        navigate('/cashier-assistance');
        break;

      case 'SUSPICIOUS_TRANSACTION':
        navigate('/suspicioustransactionalerts');
        break;
      
      case 'TRIGGER_ALERT':
        navigate('/triggeralerts');
        break;
      
      default:
        navigate('/dashboard');
        break;
    }
  };

  const getNotificationIcon = (type) => {
    const colors = {
      REPORT_UPDATE: 'bg-blue-500',
      COMMENT_MENTION: 'bg-purple-500',
      REPORT_APPROVED: 'bg-green-500',
      CLIENT_QUERY_CREATED: 'bg-orange-500',
      CLIENT_QUERY_UPDATE: 'bg-orange-400',
      STORE_UPDATE: 'bg-indigo-500',
      EMPLOYEE_UPDATE: 'bg-teal-500',
      CASHIER_ASSISTANCE: 'bg-amber-500',
      SUSPICIOUS_TRANSACTION: 'bg-red-500',
      TRIGGER_ALERT: 'bg-yellow-500',
    };
    return colors[type] || 'bg-gray-500';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleLoadMore = () => {
    if (!dropdownLoading && dropdownHasMore) {
      fetchDropdownNotifications(dropdownNotifications.length);
    }
  };

  const handleViewAll = () => {
    // ✅ Clear the page filter before navigating to ensure fresh state
    dispatch(clearNotifications());
    navigate('/notifications');
    setIsOpen(false);
  };

  return (
    <div className="notification-container" ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <Bell className="bell-icon" />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="notification-dropdown">
          {/* Header */}
          <div className="dropdown-header">
            <div className="header-title-wrapper">
              <Bell size={20} className="header-bell-icon" />
              <h3 className="dropdown-title">Notifications</h3>
            </div>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllRead}
                title="Mark all as read"
              >
                <CheckCheck size={16} />
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="notifications-list">
            {dropdownLoading && dropdownNotifications.length === 0 ? (
              <div className="loading-state">Loading notifications...</div>
            ) : dropdownNotifications.length === 0 ? (
              <div className="empty-state">
                <Bell size={48} className="empty-icon" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <>
                {dropdownNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${
                      notification.is_read ? 'read' : 'unread'
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {/* Icon */}
                    <div className={`notification-icon ${getNotificationIcon(notification.notification_type)}`}>
                      <Bell size={16} className="notification-type-icon" />
                      {!notification.is_read && <div className="unread-dot" />}
                    </div>

                    {/* Content */}
                    <div className="notification-content">
                      <h4 className="notification-title">{notification.title}</h4>
                      <p className="notification-message">{notification.message}</p>
                      <span className="notification-time">
                        {formatTime(notification.created_at)}
                      </span>
                    </div>

                    {/* Mark as read button */}
                    {!notification.is_read && (
                      <button
                        className="mark-read-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                          dispatch(markNotificationRead(notification.id));
                          // ✅ Update local state
                          setDropdownNotifications(prev => 
                            prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
                          );
                        }}
                        title="Mark as read"
                      >
                        <Check size={14} />
                      </button>
                    )}
                  </div>
                ))}

                {dropdownHasMore && (
                  <button
                    className="load-more-btn"
                    onClick={handleLoadMore}
                    disabled={dropdownLoading}
                  >
                    {dropdownLoading ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
          
          {/* View All Notifications Footer */}
          {dropdownNotifications.length > 0 && (
            <div className="dropdown-footer">
              <button
                className="view-all-btn"
                onClick={handleViewAll}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .notification-container {
          position: relative;
        }

        .notification-bell {
         padding: 8px;
          border: none;
          background: #f43f5e;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
          color: white;
        }

        .notification-bell:hover {
          background: #f63253ff;
        }

        .bell-icon {
          width: 20px;
          height: 20px;
          color: #fff;
        }

        .notification-badge {
          position: absolute;
          top: -10px;
          right: -10px;
          min-width: 10px;
          height: 20px;
          padding: 0 5px;
          background: #ef4444;
          color: white;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          animation: badge-pulse 2s ease-in-out infinite;
        }

        @keyframes badge-pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        .notification-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          width: 380px;
          max-height: 500px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .dropdown-header {
          padding: 16px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .header-title-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .header-bell-icon {
          color: #5b595aff;
        }

        .dropdown-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #111827;
        }

        .mark-all-read-btn {
          padding: 6px;
          border: none;
          background: transparent;
          color: #6b7280;
          cursor: pointer;
          border-radius: 6px;
          display: flex;
          align-items: center;
          transition: all 0.2s;
        }

        .mark-all-read-btn:hover {
          background: #f3f4f6;
          color: #f43f5e;
        }

        .notifications-list {
          flex: 1;
          overflow-y: auto;
          max-height: 432px;
        }

        .notification-item {
          padding: 12px 16px;
          display: flex;
          gap: 12px;
          cursor: pointer;
          transition: background-color 0.2s;
          border-bottom: 1px solid #f3f4f6;
        }

        .notification-item:hover {
          background: #f9fafb;
        }

        .notification-item.unread {
          background: #eff6ff;
        }

        .notification-item.unread:hover {
          background: #dbeafe;
        }

        .notification-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          flex-shrink: 0;
        }

        .notification-type-icon {
          color: white;
        }

        .unread-dot {
          position: absolute;
          top: -2px;
          right: -2px;
          width: 12px;
          height: 12px;
          background: #ef4444;
          border: 2px solid white;
          border-radius: 50%;
        }

        .notification-content {
          flex: 1;
          min-width: 0;
        }

        .notification-title {
          margin: 0 0 4px 0;
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .notification-message {
          margin: 0 0 4px 0;
          font-size: 13px;
          color: #6b7280;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .notification-time {
          font-size: 12px;
          color: #9ca3af;
        }

        .mark-read-btn {
          padding: 6px;
          border: none;
          background: transparent;
          color: #9ca3af;
          cursor: pointer;
          border-radius: 6px;
          display: flex;
          align-items: center;
          flex-shrink: 0;
          transition: all 0.2s;
        }

        .mark-read-btn:hover {
          background: #e5e7eb;
          color: #f43f5e;
        }

        .loading-state,
        .empty-state {
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .empty-icon {
          margin: 0 auto 12px;
          color: #d1d5db;
        }

        .load-more-btn {
          width: 100%;
          padding: 12px;
          border: none;
          background: transparent;
          color: #f43f5e;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .load-more-btn:hover:not(:disabled) {
          background: #f3f4f6;
        }

        .load-more-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .dropdown-footer {
          padding: 12px 16px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .view-all-btn {
          width: 100%;
          padding: 10px;
          border: none;
          background: transparent;
          color: #f43f5e;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .view-all-btn:hover {
          background: #eff6ff;
        }

        /* Scrollbar styling */
        .notifications-list::-webkit-scrollbar {
          width: 6px;
        }

        .notifications-list::-webkit-scrollbar-track {
          background: #f3f4f6;
        }

        .notifications-list::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }

        .notifications-list::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        /* Color variants for notification types */
        .bg-blue-500 { background: #f43f5e; }
        .bg-purple-500 { background: #a855f7; }
        .bg-green-500 { background: #22c55e; }
        .bg-orange-500 { background: #f97316; }
        .bg-orange-400 { background: #fb923c; }
        .bg-indigo-500 { background: #6366f1; }
        .bg-teal-500 { background: #14b8a6; }
        .bg-red-500 { background: #ef4444; }
        .bg-yellow-500 { background: #eab308; }
        .bg-gray-500 { background: #6b7280; }
      `}</style>
    </div>
  );
};

export default NotificationsDropdown;